import ActivityStatusIcon from "@components/ActivityStatusIcon/ActivityStatusIcon"
import { OUTGOING_TRANSACTION_CODE, TRANSACTION_CODE, getTransactionNameShort, getLocalizedStatus } from "@constants/transaction"
import { capitalizeText, formatDate, removeUnderscore, toCurrencyFormat } from "@lib/utils"
import {
    AmountWrapper,
    Container,
    DateText,
    DetailsWrapper,
    IconContainer,
    IconWrapper,
    LeftSide,
    Minting,
    P2PTransfer,
    PaymentMade,
    PaymentReceived,
    Redeem,
    Reward,
    RightSide,
    StatusText,
    StatusWrapper,
    TopUp,
    TransactionName,
    UnitText,
    WrapperContainer,
} from "./ActivityListItem.styles"

type Props = {
    uuid: string
    transaction_uuid?: string
    code: number
    timestamp: number
    status: string
    ui_amount?: number
    ui_fee?: number
    unit: string,
    onClick?: () => void
}

const ActivityIcon = ({ code }: { code: typeof TRANSACTION_CODE }) => {
    switch (code) {
        case 101:
        case 102:
            return <TopUp />
        case 201:
        case 202:
        case 203:
            return <Redeem />
        case 301:
            return <PaymentMade />
        case 302:
            return <PaymentReceived />
        case 601:
        case 602:
            return <P2PTransfer />
        case 701:
            return <Reward />
        case 801:
            return <Minting />
        case 901:
            return <PaymentMade />
        default:
            return null
    }
}

const StatusIcon = ({ status }: { status: string }) => {
    if (status === 'COMPLETED') {
        return <ActivityStatusIcon status="complete" strokeWidth={1} />
    }

    if ([
        'PENDING',
        'INITIATED',
        'CREATED',
        'UNDER_REVIEW',
        'PROCESSING',
        'MINTING',
        'PROCESSING_REFUND',
        'PROCESSING_DISBURSEMENT',
        'IN_PROGRESS',
        'WAITING_FOR_CONFIRMATION',
        'WAITING_FOR_DISBURSEMENT',
    ].includes(status)) {
        return <ActivityStatusIcon status="pending" strokeWidth={1} />
    }

    if (['FAILED', 'REJECTED'].includes(status)) {
        return <ActivityStatusIcon status="denied" strokeWidth={1} />
    }

    return null
}

const isNegative = (code: number): boolean => {
    return !!code && OUTGOING_TRANSACTION_CODE.includes(code)
}

const Amount = ({
    baseAmount,
    fee,
    unit,
    code
}: {
    baseAmount?: number
    fee?: number,
    unit: string,
    code: number
}) => {
    if (!baseAmount) {
        return null
    }
    let displayedAmount: number = baseAmount
    if (fee) {
        displayedAmount += fee
    }
    return (
        <AmountWrapper>
            {isNegative(code) ? '-' : '+'}{' '}
            {displayedAmount && toCurrencyFormat(displayedAmount)}{' '}
            <UnitText>{unit}</UnitText>
        </AmountWrapper>
    )
}

const ActivityListItem = ({ code, timestamp, status, ui_amount, ui_fee, unit, onClick }: Props) => {

    return (
        <WrapperContainer>
            <Container onClick={onClick}>
                <IconContainer><ActivityIcon code={code} /></IconContainer>
                <DetailsWrapper>
                    <LeftSide>
                        <TransactionName>
                            {getTransactionNameShort(code)}
                        </TransactionName>
                        <DateText>{formatDate(timestamp)}</DateText>
                    </LeftSide>
                    <RightSide $hasAmount={!!ui_amount}>
                        <Amount baseAmount={ui_amount} code={code} unit={unit} fee={ui_fee} />
                        <StatusWrapper>
                            <StatusText $status={status}>
                                {getLocalizedStatus(status)}
                            </StatusText>
                            <IconWrapper>
                                <StatusIcon status={status} />
                            </IconWrapper>
                        </StatusWrapper>
                    </RightSide>
                </DetailsWrapper>
            </Container>
        </WrapperContainer>
    )
}

export default ActivityListItem